import ROUTE from '@app/navigationApp/routes';
import FetchPortfolio from '@app/screens/FetchPortfolio/FetchPortfolio';
import BottomSheetContainer from '@app/sharedComponents/Bottomsheet/Bottomsheet';
import Button from '@app/sharedComponents/Button/Button';
import { ThemedText, ThemedView } from '@app/theme';
import { useNavigation } from '@react-navigation/native';
import { useState } from 'react';
import { View, StyleSheet, TouchableOpacity, Platform } from 'react-native';
import gs from '../../../styles/globalStyles';
import { useAppSelector } from '@app/store/hooks';
import { addCommasToNumber, trackEvent } from '@app/utils';
import { ThemedTouchableOpacity } from '@app/theme/Components';

const PortfolioSummary = ({
  navigation,
  route,
  handleContinueToCheckLimit,
  ctaLoading
}: {
  navigation: any,
  route: any,
  handleContinueToCheckLimit: () => void,
  ctaLoading: boolean
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [defaultRta, setDefaultRta] = useState('CAMS');
  const { authCAS } = useAppSelector((state) => state.application);
  const [loading, setLoading] = useState(false);

  const camsAmount = authCAS?.repositoryAssetMetadataMap?.CAMS?.availablePortfolioAmount || 0
  const KFintechAmount = authCAS?.repositoryAssetMetadataMap?.KARVY?.availablePortfolioAmount || 0

  const camsLastFetchedOn = authCAS?.repositoryAssetMetadataMap?.CAMS?.casFetchDate || 0
  const KFintechLastFetchedOn = authCAS?.repositoryAssetMetadataMap?.KARVY?.casFetchDate || 0


  // Sample data for the portfolio
  const portfolioData = authCAS && {
    totalValue: `₹${addCommasToNumber(authCAS.totalPortfolioAmount)}`,
    sources: [
      {
        id: '1',
        name: 'KFintech',
        value: KFintechAmount === 0 ? 'No portfolio fetched' : `Value: ₹${addCommasToNumber(KFintechAmount)}`,
        rta: 'KARVY',
        showView: KFintechAmount !== 0,
        showPrimaryAction: KFintechAmount === 0
      },
      {
        id: '2',
        name: 'CAMS',
        value: camsAmount === 0 ? 'No portfolio fetched' : `Value: ₹${addCommasToNumber(camsAmount)}`,
        rta: 'CAMS',
        showView: camsAmount !== 0,
        showPrimaryAction: camsAmount === 0
      },
    ],
  };

  const handleViewDetails = (sourceName: string) => {
    trackEvent('VIEW_PORTFOLIO_BUTTON_CLICKED', {
      source: sourceName
    })
    navigation.navigate(ROUTE.PORTFOLIO_FROM_RTA, { rta: sourceName, lastFetchedOn: sourceName === 'CAMS' ? camsLastFetchedOn : KFintechLastFetchedOn });
  };

  const handleRefresh = (sourceName: string) => {
    setDefaultRta(sourceName)
    setIsVisible(true);
    trackEvent('UNLOCK_LIMIT_BOTTOMSHEET_PAGE_VIEWED', {
      source: sourceName
    })
  };

  const renderGetMorePortfolio = () => {
    return (
      //@ts-expect-error
      <BottomSheetContainer
        isVisible={isVisible}
        close={() => { setIsVisible(false) }}
        closeOnBackdropPress={true}
        borderTopRightRadius={10}
        borderTopLeftRadius={10}
        //@ts-expect-error
        containerStyle={gs.setBg('#fefefe')}
      >
        <FetchPortfolio navigation={navigation} route={route}
          retryingFetchPortfolio={true}
          rtaToRetry={defaultRta}
          onSuccessFetch={()=>setIsVisible(false)}
        />
      </BottomSheetContainer>
    )
  }

  return (
    <View style={styles.container}>
      {/* Header Section */}
      <View style={styles.headerSection}>
        <ThemedText variant='b4r' color='text-secondary'>Portfolio value</ThemedText>
        <ThemedText variant='b4r' color='text-secondary'>{portfolioData?.totalValue}</ThemedText>
      </View>

      {/* Source Section */}
      <View style={styles.sourceSection}>
        <ThemedText variant='b5r' color='text-secondary'>SOURCE</ThemedText>
        {/* <View style={styles.divider} /> */}

        {/* Source Items */}
        {portfolioData?.sources.map((source, index) => (
          <View key={source.id} style={[styles.sourceItem, { borderBottomWidth: index === portfolioData.sources.length - 1 ? 1 : 0 }]}>
            <View style={styles.sourceContent}>
              {/* Left Side - Source Details */}
              <View style={styles.sourceDetails}>
                <ThemedText variant='b4r' color='text-primary'>From {source.name}</ThemedText>
                <View style={styles.valueContainer}>
                  <ThemedText variant='b4r' color='text-secondary'>{source.value}</ThemedText>
                  {
                    source.showView &&
                    <TouchableOpacity onPress={() => handleViewDetails(source.rta)}>
                      <ThemedText variant='b4s' color='text-action-primary'>{' '}View</ThemedText>
                    </TouchableOpacity>
                  }
                </View>
              </View>

              {/* Right Side - Refresh Button */}
              {
                source.showPrimaryAction ? <ThemedView
                  style={{
                    width: 140
                  }}
                >
                  <Button
                    eventName={'UNLOCK_LIMIT_GET_PORTFOLIO'}
                    additionalProperties={{RTA: source.rta}}
                    label={'Get portfolio'}
                    handlePress={async () => {
                      handleRefresh(source.rta)
                    }}
                    type={'SOLID'}
                    
                    size='MD'
                    bg='bg-primary'
                    textColor='text-primary'
                    disabled={false}
                  />
                </ThemedView> :
                  <ThemedTouchableOpacity onPress={() => {
                    trackEvent('REFRESH_PORTFOLIO_BUTTON_CLICKED', {
                      source: source.rta
                    })
                    handleRefresh(source.rta)}
                    }
                    style={{
                      ...(Platform.OS === 'web' ? {
                        outline: "none"
                      } : {})
                    }}
                    >
                    <ThemedText variant='b3s' color='text-action-primary'>{' '}Refresh portfolio</ThemedText>
                  </ThemedTouchableOpacity>
              }

            </View>
          </View>
        ))}
      </View>
      {/* Continue Button */}

      <Button
        eventName={route.name + '_RTA_FETCH_CHECK_CREDIT_LIMIT'}
        label={'Continue to check credit limit'}
        handlePress={async () => {
          setLoading(true)
          await handleContinueToCheckLimit()
          setLoading(false)
          //@todo: fix this
          // navigation.navigate("MF_PLEDGE_PORTFOLIO");
        }}
        type={'SOLID'}
        size='MD'
        bg='bg-primary'
        textColor='text-primary'
        disabled={false}
        buttonLoader={loading || ctaLoading}
      />
      <BottomSheetContainer
        key={'portfolio-summary'}
        isVisible={isVisible}
        close={() => { setIsVisible(false) }}
        closeOnBackdropPress={true}
        showCloseIcon={true}
        borderTopRightRadius={10}
        borderTopLeftRadius={10}
        //@ts-expect-error
        containerStyle={gs.setBg('#fefefe')}
      >
        <FetchPortfolio navigation={navigation} route={route}
          retryingFetchPortfolio={true}
          isFromMFPortfolio={true}
          rtaToRetry={defaultRta}
          onSuccessFetch={()=>setIsVisible(false)}
        />
      </BottomSheetContainer>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'white',
    padding: 20,
  },
  headerSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  headerTitle: {
    fontSize: 24,
    color: '#666',
    fontWeight: '500',
  },
  headerValue: {
    fontSize: 24,
    color: '#333',
    fontWeight: '600',
  },
  sourceSection: {
    marginBottom: 16,
  },
  sourceLabel: {
    fontSize: 16,
    color: '#666',
    fontWeight: '500',
    marginBottom: 8,
  },
  divider: {
    height: 1,
    backgroundColor: '#E5E7EB',
    marginBottom: 15,
  },
  sourceItem: {
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderTopWidth: 1,
    borderColor: '#E5E7EB',
    // borderRadius: 8,
    // marginBottom: 15,
    overflow: 'hidden',
  },
  sourceContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
  },
  sourceDetails: {
    flex: 1,
  },
  sourceName: {
    fontSize: 18,
    fontWeight: '500',
    color: '#333',
    marginBottom: 5,
  },
  valueContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  valueText: {
    fontSize: 16,
    color: '#666',
  },
  viewText: {
    fontSize: 16,
    color: '#2563EB',
    fontWeight: '500',
    marginLeft: 8,
  },
  refreshText: {
    fontSize: 16,
    color: '#2563EB',
    fontWeight: '500',
  },
  itemDivider: {
    height: 1,
    backgroundColor: '#E5E7EB',
  },
  continueButton: {
    backgroundColor: '#2563EB',
    borderRadius: 100,
    padding: 16,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 10,
  },
  continueText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
  },
});

export default PortfolioSummary;
